/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { logRegion } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getLogger } from 'src/core/services/telemetry/telemetryLogger';

export type MountLogger = {
    /**
     * The name of the component consumes the hook.
     */
    activitySource: string;

    /**
     * Optional on component mount event name.
     */
    startEventName?: string;

    /**
     * Optional on component unmount event name.
     */
    endEventName?: string;
};

/**
 * Logs time activity event for the mount / unmount lifecycle.
 */
export const useMountLogger = (props: MountLogger) => {
    const { activitySource, startEventName, endEventName } = props;
    const { rootStore } = useGlobalState();

    if (!activitySource) {
        return;
    }

    const currentResource = rootStore.azureStore.session.csAccountStore;
    const logger = React.useMemo(() => getLogger(activitySource), [activitySource]);
    const endEventHandler = React.useCallback(() => {
        logger.endEvent(endEventName || activitySource, false);
    }, [activitySource, endEventName]);

    React.useEffect(() => {
        logger.startEvent(startEventName || activitySource);

        if (currentResource?.region && (startEventName === 'LanguageStudioLandingPage' || activitySource === 'LanguageStudioLandingPage')) {
            logRegion(currentResource.region);
        }

        return endEventHandler;
    }, [activitySource, startEventName]);

    React.useEffect(() => {
        window.addEventListener('beforeunload', endEventHandler);

        return () => window.removeEventListener('beforeunload', endEventHandler);
    }, [endEventHandler]);
};
